import { say } from "./words.ts";

/**
 * What a page does when its WebGL context is lost (P1.9-T218). Phaser 4.2.1's renderer marks itself lost, emits
 * `losewebgl` and asks the browser to restore (`preventDefault` in `dispatchContextLost`); when the browser does, the
 * renderer rebuilds its wrapped GL objects and emits `restorewebgl`. A browser that does not restore, as after the
 * graphics driver resets, leaves the page frozen on its last frame with nothing said. The watcher shows a notice
 * while the context is gone, clears it when the context comes back, and once the wait runs out offers a reload.
 */

/**
 * The renderer's event for a lost WebGL context (`Phaser.Renderer.Events.LOSE_WEBGL`), named here so the module
 * runs without Phaser.
 */
export const LOSE_WEBGL = "losewebgl";

/**
 * The renderer's event for a WebGL context that came back (`Phaser.Renderer.Events.RESTORE_WEBGL`).
 */
export const RESTORE_WEBGL = "restorewebgl";

/**
 * How long the notice waits for the browser to restore the context before it offers a reload, in milliseconds.
 * A restore after a tab's own loss arrives within a second or two; one that has not come in this long is not coming.
 */
export const RESTORE_WAIT_MS = 10_000;

/**
 * The part of Phaser's renderer the watcher needs: its two context events. The Canvas renderer never emits them.
 */
export interface LossRenderer {
  on(event: string, listener: () => void): unknown;
  off(event: string, listener: () => void): unknown;
}

/**
 * The two states the notice shows: the context is gone and may come back, or the wait ran out.
 */
export type LossState = "waiting" | "reload";

/**
 * What the watcher shows and hides.
 */
export interface LossNotice {
  show(state: LossState): void;
  hide(): void;
}

/**
 * The timer the watcher waits with; the window's by default, a stand-in in tests.
 */
export interface LossTimer {
  set(callback: () => void, ms: number): number;
  clear(handle: number): void;
}

/**
 * The window's timer.
 */
const WINDOW_TIMER: LossTimer = {
  set: (callback, ms) => window.setTimeout(callback, ms),
  clear: (handle) => window.clearTimeout(handle),
};

/**
 * Watches a renderer for a lost context: shows the notice in its waiting state on a loss, hides it on a restore, and
 * after `waitMs` without one shows it in its reload state. A second loss while waiting keeps the first wait.
 *
 * @param renderer The game's renderer.
 * @param notice The notice to show.
 * @param timer The timer to wait with.
 * @param waitMs How long to wait for a restore.
 * @returns A function that stops watching and hides the notice.
 */
export function watchContextLoss(renderer: LossRenderer, notice: LossNotice, timer: LossTimer = WINDOW_TIMER, waitMs: number = RESTORE_WAIT_MS): () => void {
  let waiting: number | null = null;
  const lost = (): void => {
    if (waiting !== null) {
      return;
    }
    notice.show("waiting");
    waiting = timer.set(() => {
      waiting = null;
      notice.show("reload");
    }, waitMs);
  };
  const restored = (): void => {
    if (waiting !== null) {
      timer.clear(waiting);
      waiting = null;
    }
    notice.hide();
  };
  renderer.on(LOSE_WEBGL, lost);
  renderer.on(RESTORE_WEBGL, restored);
  return () => {
    renderer.off(LOSE_WEBGL, lost);
    renderer.off(RESTORE_WEBGL, restored);
    if (waiting !== null) {
      timer.clear(waiting);
      waiting = null;
    }
    notice.hide();
  };
}

/**
 * The words each state shows: the game's, or the editor's, which asks for a save before the reload since a reload
 * drops what is not saved.
 */
export type LossWording = "game" | "editor";

/**
 * Makes the notice as an element of the page: a `role="alert"` box with the state's sentence and, in the reload
 * state, a Reload button. The page styles it by its id, `context-lost`.
 *
 * @param doc The document.
 * @param parent Where the notice goes.
 * @param wording Whose words it shows.
 * @param reload What the button does; the page's reload by default.
 * @returns The notice.
 */
export function contextLossNotice(doc: Document, parent: HTMLElement, wording: LossWording, reload: () => void = () => doc.defaultView?.location.reload()): LossNotice {
  const box = doc.createElement("div");
  box.id = "context-lost";
  box.setAttribute("role", "alert");
  box.hidden = true;
  const line = doc.createElement("p");
  const button = doc.createElement("button");
  button.type = "button";
  button.textContent = say("floor.reload");
  button.addEventListener("click", () => reload());
  box.append(line, button);
  parent.append(box);
  return {
    show: (state) => {
      line.textContent = state === "waiting" ? say("floor.contextLost") : say(wording === "editor" ? "floor.contextGoneEditor" : "floor.contextGone");
      button.hidden = state === "waiting";
      box.hidden = false;
    },
    hide: () => {
      box.hidden = true;
    },
  };
}
